import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MoreHorizontal, Search, UserCog, Eye } from 'lucide-react';
import { toast } from 'sonner';
import AdminTable from '@/components/AdminTable';

const ROLE_BADGES = {
  admin:     { label: 'Admin',     style: { backgroundColor: '#fee2e2', color: '#dc2626' } },
  member:    { label: 'Member',    style: { backgroundColor: '#dbeafe', color: '#2563eb' } },
  applicant: { label: 'Applicant', style: { backgroundColor: '#fef3c7', color: '#d97706' } },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Members() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [roleDialog, setRoleDialog] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback((role) => {
    setLoading(true);
    const params = role === 'all' ? '' : `?role=${role}`;
    api.get(`/admin/members${params}`)
      .then(r => setUsers(r.data))
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsers(roleFilter); }, [roleFilter, fetchUsers]);

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openRoleDialog = (user) => {
    setRoleDialog(user);
    setNewRole(user.role);
  };

  const submitRoleChange = async () => {
    if (!roleDialog) return;
    setSubmitting(true);
    try {
      await api.put(`/admin/members/${roleDialog.id}/role`, { role: newRole });
      toast.success('Role updated successfully');
      setRoleDialog(null);
      fetchUsers(roleFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell title="Members">
      <div className="space-y-5">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <Tabs value={roleFilter} onValueChange={setRoleFilter}>
            <TabsList className="bg-gray-100 rounded-lg p-1">
              <TabsTrigger value="all" className="rounded-md">All</TabsTrigger>
              <TabsTrigger value="member" className="rounded-md">Members</TabsTrigger>
              <TabsTrigger value="applicant" className="rounded-md">Applicants</TabsTrigger>
              <TabsTrigger value="admin" className="rounded-md">Admins</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="pl-8 text-sm bg-white border-gray-200 rounded-xl focus:ring-blue-500 focus:border-blue-500 shadow-sm"
            />
          </div>
        </div>

        {/* Table */}
        <AdminTable
          data={filtered}
          loading={loading}
          dataTestId="admin-members-table"
          emptyTitle="No users found"
          emptySubtitle="Try adjusting your filters or search."
          columns={[
            {
              key: 'user', label: 'User',
              render: (u) => (
                <button onClick={() => navigate(`/admin/members/${u.id}`)} className="flex items-center gap-3 text-left transition-opacity hover:opacity-75" data-testid={`member-row-${u.id}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 bg-amber-600">
                    {u.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{u.name}</p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </div>
                </button>
              ),
            },
            { key: 'joined', label: 'Joined', render: (u) => <span className="text-xs font-medium text-gray-500">{formatDate(u.created_at)}</span> },
            {
              key: 'role', label: 'Role',
              render: (u) => {
                const badge = ROLE_BADGES[u.role] || ROLE_BADGES.applicant;
                return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold" style={badge.style}>{badge.label}</span>;
              },
            },
            { key: 'status', label: 'Status', render: (u) => <span className="text-xs font-medium text-gray-500 capitalize">{u.membership_status || '—'}</span> },
            {
              key: 'actions', label: '', width: '60px',
              render: (u) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-gray-100 rounded-xl">
                      <MoreHorizontal size={15} className="text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border border-gray-100 shadow-lg rounded-xl">
                    <DropdownMenuItem onClick={() => navigate(`/admin/members/${u.id}`)} className="gap-2 cursor-pointer hover:bg-gray-50 rounded-lg">
                      <Eye size={14} className="text-gray-500" /> <span className="text-gray-700 font-medium">View profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => openRoleDialog(u)} className="gap-2 cursor-pointer hover:bg-gray-50 rounded-lg">
                      <UserCog size={14} className="text-gray-500" /> <span className="text-gray-700 font-medium">Change role</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </div>

      {/* Role Change Dialog */}
      <Dialog open={!!roleDialog} onOpenChange={() => setRoleDialog(null)}>
        <DialogContent className="bg-white border-0 shadow-xl rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Change role — {roleDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">New role</label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="bg-white border-gray-200 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-100 shadow-lg rounded-xl">
                <SelectItem value="applicant" className="hover:bg-gray-50 cursor-pointer">Applicant</SelectItem>
                <SelectItem value="member" className="hover:bg-gray-50 cursor-pointer">Member</SelectItem>
                <SelectItem value="admin" className="hover:bg-gray-50 cursor-pointer">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRoleDialog(null)} disabled={submitting} className="rounded-xl font-semibold text-gray-600 hover:bg-gray-100">Cancel</Button>
            <Button
              onClick={submitRoleChange}
              disabled={submitting || newRole === roleDialog?.role}
              className="rounded-xl font-semibold shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? 'Saving…' : 'Save role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
